import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  FileQuestion,
  Lock,
  CreditCard,
  FileText,
  Wifi,
  Calendar,
  MessageCircle,
  ChevronRight,
  ShieldCheck,
  ThumbsUp,
  Phone,
  Mail,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { SupportChatPopup } from '@/components/support/SupportChatPopup'
import { useLanguage } from '@/contexts/LanguageContext'
import { traducoesSuporte } from '@/i18n/suporte'

const FAQ_ICONS = [FileText, Lock, FileText, CreditCard, Wifi, Calendar]

export default function Suporte() {
  const { lang, setLang } = useLanguage()
  const t = traducoesSuporte[lang]
  const [busca, setBusca] = useState('')

  const handleBuscar = () => {
    if (busca.trim()) {
      // Abre o chat flutuante com a busca como primeira mensagem seria via contexto/state
      // Por simplicidade, apenas abrimos o chat; o usuário pode colar a dúvida
      setBusca('')
    }
  }

  return (
    <div
      className="min-h-screen bg-background flex flex-col"
      style={{
        fontFamily: "'Montserrat', system-ui, sans-serif",
        backgroundImage:
          'radial-gradient(ellipse 70% 40% at 50% 0%, hsl(249 58% 51% / 0.06) 0%, transparent 60%), radial-gradient(ellipse 50% 30% at 100% 100%, hsl(237 92% 74% / 0.05) 0%, transparent 60%)',
      }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border/50 bg-card/60 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: '#4b36cc' }}>
            <span className="text-primary-foreground text-sm font-bold leading-none">V</span>
          </div>
          <div className="leading-none">
            <p className="text-sm font-bold text-foreground tracking-tight">Venda Todo Santo Dia</p>
            <p className="text-[11px] text-muted-foreground">{t.headerSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          {(["pt", "en", "es"] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-2 py-1 rounded transition-colors ${
                lang === l ? "text-[#3c3939] font-semibold" : "hover:text-[#3c3939] opacity-70"
              }`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      {/* Hero + Busca */}
      <section className="flex flex-col items-center px-4 py-8 sm:py-12">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight text-center">
          {t.heroTitle}
        </h1>
        <div className="w-full max-w-2xl mt-6 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={t.searchPlaceholder}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
              className="pl-10 h-11 rounded-lg bg-card border-border"
            />
          </div>
          <Button
            onClick={handleBuscar}
            className="h-11 px-6 text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#4b36cc' }}
          >
            {t.searchButton}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground text-center">
          {t.heroHint}
        </p>
      </section>

      {/* Dúvidas frequentes */}
      <section className="px-4 sm:px-6 py-8 border-t border-border/30 bg-background/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-6">
            <FileQuestion className="h-5 w-5" style={{ color: '#4b36cc' }} />
            {t.faqTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FAQ_ICONS.map((Icon, i) => (
              <Card
                key={i}
                className="p-4 rounded-xl border-border/60 bg-card hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-foreground">{t.faqItems[i].pergunta}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.faqItems[i].descricao}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Button variant="link" className="text-primary gap-1" asChild>
              <Link to="/ajuda">
                {t.needAnotherHelp}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Banner Central de Ajuda — destaque (rosa/acento suave, identidade verde) */}
      <section className="px-4 sm:px-6 py-10 bg-primary/10 border-y border-primary/10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
              <MessageCircle className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                {t.bannerTitle}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t.bannerDescription}
              </p>
            </div>
          </div>
          <Button asChild className="bg-primary hover:bg-primary/90 shrink-0">
            <Link to="/ajuda" className="gap-2">
              {t.bannerCta}
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Confiança */}
      <section className="px-4 sm:px-6 py-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-semibold text-foreground mb-6">{t.trustTitle}</h2>
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-success/15 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="font-medium text-foreground">{t.trustSupport}</p>
                <p className="text-sm text-muted-foreground">
                  {t.trustDescription}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-success/10 border border-success/20">
              <ThumbsUp className="h-8 w-8 text-success" />
              <span className="font-semibold text-foreground">{t.trustGreat}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Verificar / Contato */}
      <section className="px-4 sm:px-6 py-10 bg-muted/30 border-t border-border/30">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-lg font-semibold text-foreground mb-2">{t.verificarTitle}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t.verificarDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <Input
              placeholder={t.verificarPlaceholder}
              className="flex-1 h-11 bg-background"
            />
            <Button variant="secondary" className="h-11 bg-primary/10 text-primary hover:bg-primary/20 border-0">
              {t.verificarButton}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground text-xs font-bold">V</span>
                </div>
                <span className="font-semibold text-foreground text-sm">{t.footerBrand}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t.footerTagline}</p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">{t.footerProducts}</h4>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><Link to="/ajuda" className="hover:text-foreground transition-colors">{t.footerFaq}</Link></li>
                <li><a href="#" className="hover:text-foreground transition-colors">{t.footerSecure}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">{t.footerChannels}</h4>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><a href="mailto:suporte@vendatodosantodia.com.br" className="hover:text-foreground transition-colors flex items-center gap-1"><Mail className="h-3 w-3" /> {t.footerEmail}</a></li>
                <li><span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {t.footerHours}</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-3">{t.footerTerms}</h4>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li><a href="#" className="hover:text-foreground transition-colors">{t.footerPrivacy}</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">{t.footerTermsUse}</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-border/50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {t.footerBrand}. {t.footerCopyright}
            </p>
            <p className="text-xs text-muted-foreground">
              {t.footerAttendance}
            </p>
          </div>
        </div>
      </footer>

      {/* Chat flutuante */}
      <SupportChatPopup />
    </div>
  )
}
